import { useState, useCallback, useMemo, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

interface LastOpenedEntry {
  projectId: string;
  openedAt: number;
}

const MAX_ENTRIES = 6;
/** Keeps all hook instances (e.g. ⌘K and the dashboard) in sync. */
const CHANGED_EVENT = "last-opened-projects:changed";

function getStorageKey(userId: string) {
  return `last-opened-projects:${userId}`;
}

function readEntries(storageKey: string): LastOpenedEntry[] {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function useLastOpenedProjects() {
  const { user } = useAuth();
  const storageKey = user ? getStorageKey(user.id) : null;

  const [entries, setEntries] = useState<LastOpenedEntry[]>(() => {
    if (!storageKey) return [];
    return readEntries(storageKey);
  });

  // Re-initialize when user changes, and follow changes made elsewhere.
  useEffect(() => {
    if (!storageKey) {
      setEntries([]);
      return;
    }
    const reload = () => setEntries(readEntries(storageKey));
    reload();
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey) reload();
    };
    window.addEventListener(CHANGED_EVENT, reload);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGED_EVENT, reload);
      window.removeEventListener("storage", onStorage);
    };
  }, [storageKey]);

  const recordOpen = useCallback(
    (projectId: string) => {
      if (!storageKey) return;
      const prev = readEntries(storageKey);
      const filtered = prev.filter((e) => e.projectId !== projectId);
      const next = [{ projectId, openedAt: Date.now() }, ...filtered].slice(
        0,
        MAX_ENTRIES,
      );
      setEntries(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
        window.dispatchEvent(new Event(CHANGED_EVENT));
      } catch {
        // Storage full or unavailable — silently degrade
      }
    },
    [storageKey],
  );

  const lastOpenedIds = useMemo(
    () => entries.map((e) => e.projectId),
    [entries],
  );

  return { recordOpen, lastOpenedIds };
}
